import classNames from "classnames/bind";
import React, { useEffect, useMemo, useState } from "react";
import { type IconVariants } from "../Icon/types";
import { type PillProps } from "../Pill/types";
import { Icon } from "../Icon";
import { Pill } from "../Pill";
import { shouldDisplayPillDot } from "../Pill/Pill";
import { Popover } from "../Popover";
import styles from "./PillPopover.module.scss";
import { PillPopoverType } from "./types";

const cx = classNames.bind(styles);

const Display = (
  props: {
    isEditable?: boolean;
    label: Pick<PillProps, "children"> | string;
    icon?: IconVariants | null;
  } & Pick<PillProps, "variant"> &
    Partial<Pick<PillProps, "isLoading" | "isBusy" | "isDisabled" | "hasDot">>
) => {
  const {
    variant,
    isLoading,
    isBusy,
    isDisabled,
    hasDot,
    isEditable,
    label,
    icon,
  } = props;
  return (
    <Pill
      variant={variant ?? "default"}
      isLoading={isLoading}
      isBusy={isBusy}
      isDisabled={isDisabled}
      hasDot={hasDot}
      iconLeft={
        icon ? (
          <Icon
            variant={icon}
            size={14}
          />
        ) : null
      }
      iconRight={
        isEditable && (
          <Icon
            variant="caret-down"
            weight="fill"
            size={14}
          />
        )
      }
    >
      {label}
    </Pill>
  );
};

const PillPopover = <T,>(props: PillPopoverType<T>) => {
  const {
    options,
    onChange,
    value,
    isEditable,
    isDisabled,
    isBusy,
    isLoading,
    componentOverride,
  } = props;

  const [open, setOpen] = useState<boolean>(false);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  useEffect(() => {
    if ((isBusy || isLoading || isDisabled || !isEditable) && open) {
      setOpen(false);
    }
  }, [isBusy, isLoading, isDisabled]);

  const [hoveredOptionValue, setHoveredOptionValue] = useState<T>();

  const triggerComponent = (
    <Display
      label={selectedOption?.label ?? ""}
      variant={selectedOption?.variant ?? "default"}
      hasDot={!selectedOption?.icon === null}
      icon={selectedOption?.icon}
      isBusy={isBusy}
      isDisabled={isDisabled}
      isEditable={isEditable}
      isLoading={isLoading}
    />
  );

  if (!isEditable)
    return componentOverride ? <>{componentOverride}</> : triggerComponent;

  const hasAnyIconOrDotContainer = options.some(
    (option) => !!option.icon || shouldDisplayPillDot(option?.variant)
  );

  return (
    <Popover
      rootProps={{
        open: open,
        onOpenChange: setOpen,
      }}
      contentClassName={cx("popover-content")}
      contentRender={() => (
        <>
          <ul
            className={cx("options-list")}
            role="listbox"
          >
            {options.map((option) => {
              const isSelected = value === option.value;
              const isHovered = option.value === hoveredOptionValue;

              return (
                <li
                  className={cx(
                    "option",
                    (isSelected || isHovered) && (option?.variant ?? "default")
                  )}
                  role="option"
                  aria-label={`Select option ${option.label}`}
                  aria-selected={isSelected}
                  onMouseEnter={() => {
                    setHoveredOptionValue(option.value);
                  }}
                  onMouseLeave={() => {
                    setHoveredOptionValue(undefined);
                  }}
                  onClick={() => {
                    setOpen(false);
                    onChange(option.value);
                    setHoveredOptionValue(undefined);
                  }}
                  key={
                    typeof option.value === "string"
                      ? option.value
                      : option.label
                  }
                >
                  {hasAnyIconOrDotContainer && (
                    <div className={cx("icon-container")}>
                      {option.icon ? (
                        <div className={cx("icon", option.variant)}>
                          <Icon
                            variant={option.icon}
                            size={14}
                          />
                        </div>
                      ) : shouldDisplayPillDot(option?.variant) &&
                        option.icon !== null ? (
                        <div className={cx("pillIndicator", option.variant)} />
                      ) : null}
                    </div>
                  )}
                  <span>{option.label}</span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    >
      {componentOverride ? (
        componentOverride
      ) : (
        <button
          className={cx("popover-trigger")}
          disabled={!isEditable || !!isBusy || !!isLoading || !!isDisabled}
        >
          {triggerComponent}
        </button>
      )}
    </Popover>
  );
};

PillPopover.Display = Display;

export default PillPopover;
